import Phaser from 'phaser';
import { GameMode } from './GameMode.js';

export class CastleAttackMode extends GameMode {
  constructor() {
    super('Castle Attack', 'Teams race to destroy the central castle');
    this.availableTeams = ['red', 'blue', 'green', 'yellow', 'purple'];
    this.teamColors = {
      'red': '#ff4444',
      'blue': '#4444ff', 
      'green': '#44ff44',
      'yellow': '#ffff44',
      'purple': '#ff44ff'
    };
    this.numTeams = 2; // Default to 2 teams, can be set via config
    this.castle = null;
    this.walls = [];
  }

  initialize(scene) {
    // Get team count from scene config if available
    if (scene.teamCount && scene.teamCount >= 2 && scene.teamCount <= 5) {
      this.numTeams = scene.teamCount;
    }
    console.log(`[GameMode] Initialized Castle Attack mode with ${this.numTeams} teams`);
    this.createCastle(scene);
    this.createWalls(scene);
  }

  createCastle(scene) {
    // TODO: Create castle entity at map center
    const worldBounds = scene.gameManager?.worldBounds || { x: 0, y: 0, width: 2048, height: 1152 };
    const centerX = worldBounds.x + worldBounds.width / 2;
    const centerY = worldBounds.y + worldBounds.height / 2;
    
    // Placeholder castle (will be replaced with proper graphics)
    this.castle = {
      x: centerX,
      y: centerY,
      health: 1000,
      maxHealth: 1000,
      sprite: null // TODO: Create castle sprite
    };
    
    console.log('[GameMode] Castle created at center');
  }

  createWalls(scene) {
    // TODO: Create wall blocks around castle
    if (!this.castle) return;
    
    const wallCount = 8;
    const wallDistance = 150;
    const wallHealth = 250;
    
    this.walls = [];
    for (let i = 0; i < wallCount; i++) {
      const angle = (i / wallCount) * Math.PI * 2;
      const x = this.castle.x + Math.cos(angle) * wallDistance;
      const y = this.castle.y + Math.sin(angle) * wallDistance;
      
      // TODO: Create wall block sprite (grey rectangle for now)
      const wall = {
        x: x,
        y: y,
        health: wallHealth,
        maxHealth: wallHealth,
        sprite: null // TODO: Create wall sprite
      };
      
      this.walls.push(wall);
    }
    
    console.log(`[GameMode] Created ${this.walls.length} wall blocks`);
  }

  spawnPlayers(scene, players) {
    // Use configured team count, but ensure we have enough players
    const numTeams = Math.min(this.numTeams, Math.max(2, Math.ceil(players.length / 4))); // At least 4 players per team
    const selectedTeams = this.availableTeams.slice(0, numTeams);
    
    players.forEach((player, index) => {
      const teamIndex = index % numTeams;
      const teamId = selectedTeams[teamIndex];
      const teamColor = this.teamColors[teamId];
      player.setTeam(teamId, teamColor);
    });

    // Spawn teams at map corners
    const worldBounds = scene.gameManager?.worldBounds || { x: 0, y: 0, width: 2048, height: 1152 };
    const spawnZones = this.getCornerSpawnZones(worldBounds, numTeams);
    
    selectedTeams.forEach((teamId, teamIndex) => {
      const teamPlayers = players.filter(p => p.team === teamId);
      const spawnZone = spawnZones[teamIndex];
      
      teamPlayers.forEach(player => {
        const x = Phaser.Math.Between(spawnZone.x, spawnZone.x + spawnZone.width);
        const y = Phaser.Math.Between(spawnZone.y, spawnZone.y + spawnZone.height);
        
        player.sprite.x = x;
        player.sprite.y = y;
        player.targetX = x;
        player.targetY = y;
      });
    });
    
    console.log(`[GameMode] Spawned ${players.length} players in ${numTeams} teams for Castle Attack`);
  }

  getCornerSpawnZones(worldBounds, numTeams) {
    const zones = [];
    const margin = 100;
    const zoneSize = 200;
    
    if (numTeams >= 2) {
      // Top-left
      zones.push({
        x: worldBounds.x + margin,
        y: worldBounds.y + margin,
        width: zoneSize,
        height: zoneSize
      });
      // Top-right
      zones.push({
        x: worldBounds.x + worldBounds.width - margin - zoneSize,
        y: worldBounds.y + margin,
        width: zoneSize,
        height: zoneSize
      });
    }
    
    if (numTeams >= 3) {
      // Bottom-left
      zones.push({
        x: worldBounds.x + margin,
        y: worldBounds.y + worldBounds.height - margin - zoneSize,
        width: zoneSize,
        height: zoneSize
      });
    }
    
    if (numTeams >= 4) {
      // Bottom-right
      zones.push({
        x: worldBounds.x + worldBounds.width - margin - zoneSize,
        y: worldBounds.y + worldBounds.height - margin - zoneSize,
        width: zoneSize,
        height: zoneSize
      });
    }
    
    if (numTeams >= 5) {
      // Center-top (for 5th team)
      zones.push({
        x: worldBounds.x + worldBounds.width / 2 - zoneSize / 2,
        y: worldBounds.y + margin,
        width: zoneSize,
        height: zoneSize
      });
    }
    
    return zones;
  }

  checkCombat(scene) {
    // TODO: Implement castle attack combat
    // Players attack walls and castle
    // Players can't attack teammates
    console.log('[GameMode] Castle Attack combat not yet implemented');
  }

  checkWinCondition(scene) {
    // TODO: Check if castle destroyed or all players dead
    if (this.castle && this.castle.health <= 0) {
      // Find which team dealt the killing blow
      console.log('Castle destroyed!');
      return { winner: null, type: 'castle_destroyed' };
    }
    
    const alivePlayers = scene.players.filter(p => p.isAlive);
    if (alivePlayers.length === 0 && scene.gameState === 'playing') {
      console.log('Castle Attack match lost - all players died');
      return { winner: null, type: 'defeat' };
    }
    
    return null; // No winner yet
  }

  getPlayerTarget(player, allPlayers) {
    // TODO: Target nearest wall or castle instead of other players
    // Simple pathfinding: move toward castle, attack walls when blocked
    return null;
  }
}
